/**
 * Admin panel talks to admin-service (REST + Socket.IO), not api-service.
 * Production: https://qmpp.rajkumaryadav.online
 * Local dev: admin-service = 4041, api-service = 4040.
 */
const DEFAULT_ADMIN_SERVICE_ORIGIN =
  process.env.NODE_ENV === 'production'
    ? 'https://qmpp.rajkumaryadav.online'
    : 'http://localhost:4041';

/** api-service local port — Socket.IO and /v1/admin live on admin-service instead. */
const API_SERVICE_DEV_PORT = '4040';
const ADMIN_SERVICE_DEV_PORT = '4041';

/**
 * Strip trailing slash and accidental `/v1/admin` suffix from a base URL.
 * @param {string} rawUrl
 * @returns {string}
 */
function normalizeBaseUrl(rawUrl) {
  let url = String(rawUrl || '').trim();
  if (!url) return DEFAULT_ADMIN_SERVICE_ORIGIN;
  if (url.endsWith('/')) {
    url = url.slice(0, -1);
  }
  return url.replace(/\/v1\/admin$/i, '');
}

/**
 * Read a `REACT_APP_*` env var (empty string when unset or not in a Node build).
 * @param {string} name
 * @returns {string}
 */
function readEnv(name) {
  if (typeof process === 'undefined') return '';
  return String(process.env[name] || '').trim();
}

/**
 * If REACT_APP_API_URL mistakenly targets api-service (:4040), use admin-service (:4041).
 */
function ensureAdminServiceOrigin(url) {
  try {
    const parsed = new URL(url);
    if (parsed.port === API_SERVICE_DEV_PORT) {
      parsed.port = ADMIN_SERVICE_DEV_PORT;
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(
          `[admin-panel] ${url} is api-service; admin REST and realtime use admin-service (${parsed.origin}). ` +
            'Set REACT_APP_API_URL=http://localhost:4041 or REACT_APP_REALTIME_URL for Socket.IO only.',
        );
      }
      return parsed.origin;
    }
  } catch {
    /* ignore invalid URL */
  }
  return url;
}

/**
 * REST base URL for admin-service (not api-service).
 * @returns {string}
 */
export function getAdminApiBaseUrl() {
  const raw = readEnv('REACT_APP_API_URL') || DEFAULT_ADMIN_SERVICE_ORIGIN;
  return ensureAdminServiceOrigin(normalizeBaseUrl(raw));
}

/**
 * Socket.IO namespace is served by admin-service (same host as REST by default).
 * @returns {string}
 */
export function getRealtimeSocketBaseUrl() {
  const explicit = readEnv('REACT_APP_REALTIME_URL');
  const raw = explicit || getAdminApiBaseUrl();
  return normalizeBaseUrl(raw);
}

/**
 * Realtime SSE endpoint URL served by admin-service (/v1/admin/realtime/events).
 * @returns {string}
 */
export function getRealtimeSseUrl() {
  const base = getRealtimeSocketBaseUrl();
  return `${base}/v1/admin/realtime/events`;
}

/**
 * Public customer site origin (privacy / terms HTML pages are served by api-service).
 * @returns {string}
 */
export function getPublicSiteOrigin() {
  const explicit = readEnv('REACT_APP_PUBLIC_SITE_URL');
  if (explicit) return normalizeBaseUrl(explicit);
  if (process.env.NODE_ENV === 'production') {
    return 'https://qmpp.rajkumaryadav.online';
  }
  return `http://localhost:${API_SERVICE_DEV_PORT}`;
}
